import React, { useState } from "react";
import { Form, Button, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { authenticationService } from "@/api/authentication";
import { PasswordInputWithValidation } from "@/features/auth";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { useAuth } from "@/shared/contexts/AuthContext";

const ErrorMessage = styled.div`
	background: #fff2f0;
	border: 1px solid #ffccc7;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 1.5rem;
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	color: #ff4d4f;
	font-size: 0.875rem;

	.error-icon {
		color: #ff4d4f;
		font-size: 1rem;
		margin-top: 0.125rem;
	}

	.error-content {
		flex: 1;
	}

	.error-title {
		font-weight: 700;
		color: #000;
		margin-bottom: 0.25rem;
	}

	.error-message {
		color: #000;
		font-size: 0.875rem;
	}
`;

const StyledForm = styled(Form)`
	margin-top: 1.5rem;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	.ant-form-item {
		margin-bottom: 0.5rem;
	}

	.ant-form-item:last-child {
		margin-top: 1rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 36px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StyledPasswordInput = styled(PasswordInputWithValidation)`
	.ant-input-affix-wrapper {
		height: 36px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}
`;

const SetPasswordButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	width: 100%;
	background: #d9d9d9;
	border: none;
	color: #666;

	&:hover:not(:disabled) {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		color: white;
	}

	&.ant-btn-primary {
		background: ${CUSTOM_COLORS.PRIMARY};
		color: white;
	}

	&[disabled],
	&.ant-btn-primary[disabled] {
		background: #d9d9d9 !important;
		color: #666 !important;
		border: none !important;
		opacity: 1;
		cursor: not-allowed;
	}
`;

const OrganizationResetPassword: React.FC = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { login } = useAuth();
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isPasswordValid, setIsPasswordValid] = useState(false);
	const [isConfirmValid, setIsConfirmValid] = useState(false);

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPassword(value);
		const validation = validatePassword(value);
		setIsPasswordValid(validation.isValid);
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setConfirmPassword(value);
		setIsConfirmValid(value === password && value.length > 0);
	};

	const onFinish = async (values: any) => {
		setLoading(true);
		setErrorMessage("");

		try {
			const organizationEmail = localStorage.getItem("organization_email");
			if (!organizationEmail) {
				setErrorMessage("Organization email not found. Please login again.");
				setLoading(false);
				return;
			}

			const response = await authenticationService.organizationResetPassword({
				email: organizationEmail,
				newPassword: values.password,
			});

			login(response.user);
			message.success("Password set successfully! Redirecting to profile setup...");
			localStorage.removeItem("organization_email");
			setTimeout(() => {
				navigate("/amplify/update-user");
			}, 2000);
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || "Failed to reset password. Please try again.";
			setErrorMessage(errorMsg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthLayout title="Reset your Password" subtitle="Let's add a new password" isOrganization={true}>
			{errorMessage && (
				<ErrorMessage>
					<CloseCircleOutlined className="error-icon" />
					<div className="error-content">
						<div className="error-title">Error</div>
						<div className="error-message">{errorMessage}</div>
					</div>
				</ErrorMessage>
			)}

			<StyledForm form={form} name="resetPassword" onFinish={onFinish} layout="vertical">
				<Form.Item
					name="password"
					label="Password"
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please enter a password"));
								}
								const validation = validatePassword(value);
								if (!validation.isValid) {
									return Promise.reject(new Error(validation.errors[0]));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={password}
						onChange={handlePasswordChange}
						placeholder="Enter Password"
						size="large"
						showValidation={true}
					/>
				</Form.Item>

				<Form.Item
					name="confirmPassword"
					label="Confirm password"
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please confirm your password"));
								}
								if (value !== password) {
									return Promise.reject(new Error("Passwords do not match!"));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						placeholder="Re-enter Password"
						size="large"
						showValidation={false}
					/>
				</Form.Item>

				<Form.Item>
					<SetPasswordButton
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={!isPasswordValid || !isConfirmValid}
					>
						Set password
					</SetPasswordButton>
				</Form.Item>
			</StyledForm>
		</AuthLayout>
	);
};

export default OrganizationResetPassword;
